/**
 * @fileoverview
 * `featureRegistry` — singleton qui agrège tous les manifests et expose des
 * méthodes de query pour le shell.
 *
 * @remarks
 * Le shell n'importe **jamais** une feature directement. Il appelle des
 * méthodes du registry qui retournent ce qu'il faut rendre, déjà filtré par
 * `enabledWhen`. C'est l'inversion de dépendance — le shell dépend du contrat
 * (`types.ts`), pas des implémentations.
 *
 * Le registry est instancié au boot (`main.tsx`), peuplé par
 * `features-registration.ts`, puis verrouillé par `markInitialized()`.
 */

import type { Context } from './contexts';
import type {
  AppContext,
  FeatureManifest,
  LazyView,
  ResolvedNavigationLink,
  ShortcutBinding,
  TabConfig,
} from './types';

// ════════════════════════════════════════════════════════════════════════════
// Erreurs custom — fail-loud avec messages dev-friendly
// ════════════════════════════════════════════════════════════════════════════

export class FeatureRegistryError extends Error {
  constructor(message: string) {
    super(`[FeatureRegistry] ${message}`);
    this.name = 'FeatureRegistryError';
  }
}

// ════════════════════════════════════════════════════════════════════════════
// Singleton
// ════════════════════════════════════════════════════════════════════════════

class FeatureRegistry {
  private features = new Map<string, FeatureManifest>();
  private initialized = false;

  // ── Phase d'enregistrement ────────────────────────────────────────────────

  /**
   * Enregistre une feature. À appeler avant `markInitialized()`.
   *
   * @throws Si la feature est enregistrée après `markInitialized`.
   * @remarks Si une feature avec le même ID est déjà enregistrée, warning + skip.
   */
  register(manifest: FeatureManifest): void {
    if (this.initialized) {
      throw new FeatureRegistryError(
        `Cannot register feature "${manifest.id}" after initialization. ` +
          `Tous les register() doivent être faits dans features-registration.ts ` +
          `avant le markInitialized().`,
      );
    }

    if (this.features.has(manifest.id)) {
      // eslint-disable-next-line no-restricted-syntax -- warning dev légitime sur duplicate register
      console.warn(
        `[FeatureRegistry] Feature "${manifest.id}" already registered. Skipping duplicate.`,
      );
      return;
    }

    this.features.set(manifest.id, manifest);
  }

  /**
   * Verrouille le registry et valide les invariants globaux.
   *
   * @param globalContexts - Liste exhaustive des contextes qui DOIVENT avoir
   *                         au moins un `centerView` couvrant. En général
   *                         `ALL_CONTEXTS` exporté de `contexts.ts`.
   * @throws Si une dépendance déclarée n'est pas enregistrée.
   * @throws Si un contexte global n'a aucun `centerView`.
   */
  markInitialized(globalContexts: readonly Context[]): void {
    this.validateDependencies();
    this.validateCenterViewCoverage(globalContexts);
    this.validateRoutePathUniqueness();
    this.validateNavigationIdUniqueness();
    this.initialized = true;
  }

  private validateDependencies(): void {
    for (const manifest of this.features.values()) {
      if (!manifest.dependencies) continue;
      for (const depId of manifest.dependencies) {
        if (!this.features.has(depId)) {
          throw new FeatureRegistryError(
            `Feature "${manifest.id}" déclare une dépendance "${depId}" qui ` +
              `n'est pas enregistrée. Vérifie features-registration.ts.`,
          );
        }
      }
    }
  }

  private validateCenterViewCoverage(globalContexts: readonly Context[]): void {
    const orphanContexts: Context[] = [];

    for (const ctx of globalContexts) {
      const hasCenterView = Array.from(this.features.values()).some(
        (m) => m.centerView?.[ctx] !== undefined,
      );
      if (!hasCenterView) orphanContexts.push(ctx);
    }

    if (orphanContexts.length > 0) {
      throw new FeatureRegistryError(
        `Aucune feature ne fournit de centerView pour les contextes suivants : ` +
          `${orphanContexts.join(', ')}. Soit tu retires ces contextes de CONTEXTS, ` +
          `soit tu ajoutes une feature qui les couvre.`,
      );
    }
  }

  /**
   * Vérifie que les `path` de routes (sur le même niveau d'arbre) sont uniques
   * **par zone**. Sans ça, si deux features exposent `{ path: '/admin' }` dans
   * la zone `'authenticated'`, react-router prend la première et ignore la
   * seconde silencieusement → bug très difficile à débugger.
   */
  private validateRoutePathUniqueness(): void {
    const seen = new Map<string, string>(); // `${zone}::${path}` → featureId

    for (const manifest of this.features.values()) {
      for (const set of manifest.routes ?? []) {
        const zone = set.zone ?? 'authenticated';
        for (const route of set.routes) {
          if (route.path === undefined) continue; // index routes / pathless layout
          const key = `${zone}::${route.path}`;
          const owner = seen.get(key);
          if (owner !== undefined && owner !== manifest.id) {
            throw new FeatureRegistryError(
              `Conflit de route : path "${route.path}" (zone "${zone}") est déclaré ` +
                `par "${owner}" ET par "${manifest.id}". React Router prendrait la ` +
                `première silencieusement. Renomme ou réattribue à une seule feature.`,
            );
          }
          seen.set(key, manifest.id);
        }
      }
    }
  }

  /**
   * Vérifie que les `id` de navigation sidebar sont uniques entre features.
   * Sans ça, deux liens avec le même id rendent un warning React (key) et
   * peuvent cacher un bug logique (lien obsolète qui survit).
   */
  private validateNavigationIdUniqueness(): void {
    const seen = new Map<string, string>(); // navigationId → featureId

    for (const manifest of this.features.values()) {
      for (const link of manifest.navigation ?? []) {
        const owner = seen.get(link.id);
        if (owner !== undefined && owner !== manifest.id) {
          throw new FeatureRegistryError(
            `Conflit de navigation : id "${link.id}" est déclaré par "${owner}" ` +
              `ET par "${manifest.id}". Renomme un des deux.`,
          );
        }
        seen.set(link.id, manifest.id);
      }
    }
  }

  // ── Phase de query (runtime, pendant la session) ─────────────────────────

  /**
   * Toutes les features enregistrées (sans filtre).
   * Utile pour les outils d'introspection (debug, doc générée).
   */
  getAllFeatures(): readonly FeatureManifest[] {
    return Array.from(this.features.values());
  }

  /**
   * Évalue si une feature est active dans l'`appCtx` courant.
   * Toutes les conditions de `enabledWhen` sont AND.
   */
  isEnabled(manifest: FeatureManifest, appCtx: AppContext): boolean {
    const w = manifest.enabledWhen;
    if (!w) return true;

    if (w.roles && w.roles.length > 0) {
      if (!appCtx.user) return false;
      const userRoles = appCtx.user.roles;
      const hasRole = w.roles.some((r) => userRoles.includes(r));
      if (!hasRole) return false;
    }

    if (w.permissions && w.permissions.length > 0) {
      if (!appCtx.user) return false;
      const userPerms = appCtx.user.permissions;
      const hasPerm = w.permissions.some((p) => userPerms.includes(p));
      if (!hasPerm) return false;
    }

    if (w.featureFlag !== undefined) {
      if (!appCtx.flags[w.featureFlag]) return false;
    }

    if (w.predicate && !w.predicate(appCtx)) return false;

    return true;
  }

  /**
   * Features actives sur un contexte donné, filtrées par `enabledWhen`.
   */
  getActiveFeatures(context: Context, appCtx: AppContext): readonly FeatureManifest[] {
    return this.getAllFeatures().filter(
      (m) => m.contexts.includes(context) && this.isEnabled(m, appCtx),
    );
  }

  /**
   * Onglets gauche pour un contexte donné, après filtrage `enabledWhen` au
   * niveau feature. Le filtrage `TabConfig.enabledWhen` (visibilité par tab)
   * est appliqué par le composant qui rend les tabs (le shell).
   */
  getLeftPanelTabs(context: Context, appCtx: AppContext): readonly TabConfig[] {
    return this.getActiveFeatures(context, appCtx).flatMap(
      (m) => m.panels?.left?.tabs[context] ?? [],
    );
  }

  getRightPanelTabs(context: Context, appCtx: AppContext): readonly TabConfig[] {
    return this.getActiveFeatures(context, appCtx).flatMap(
      (m) => m.panels?.right?.tabs[context] ?? [],
    );
  }

  /**
   * Vue centrale pour un contexte. Si plusieurs features en fournissent une,
   * la première gagne (le registry est ordonné selon l'ordre de `register`).
   */
  getCenterView(context: Context, appCtx: AppContext): LazyView | null {
    for (const m of this.getActiveFeatures(context, appCtx)) {
      const view = m.centerView?.[context];
      if (view) return view;
    }
    return null;
  }

  /**
   * Tous les liens de navigation, agrégés depuis les features actives.
   * Le shell trie ensuite par `position` puis `order`.
   *
   * @remarks
   * `activeContexts` est résolu ici : un lien qui ne le déclare pas hérite des
   * `contexts` de sa feature. L'état actif dans la sidebar se décide donc sur
   * le contexte courant, jamais sur un préfixe d'URL (cf. `NavigationLink`).
   */
  getAllNavigationLinks(appCtx: AppContext): readonly ResolvedNavigationLink[] {
    return this.getAllFeatures()
      .filter((m) => this.isEnabled(m, appCtx))
      .flatMap((m) =>
        (m.navigation ?? []).map((link) => ({
          ...link,
          activeContexts: link.activeContexts ?? m.contexts,
        })),
      );
  }

  /**
   * Composants à injecter dans le `Header` global pour le contexte courant.
   * Plusieurs features peuvent contribuer simultanément.
   */
  getHeaderSlots(context: Context, appCtx: AppContext): readonly LazyView[] {
    return this.getActiveFeatures(context, appCtx)
      .map((m) => m.headerSlot?.[context])
      .filter((v): v is LazyView => v !== undefined);
  }

  /**
   * Slots de header **globaux** : rendus sur tous les contextes, filtrés
   * uniquement par `enabledWhen` (pas par `contexts`). Pour les éléments
   * transverses (ex. compte à rebours de l'attempt actif). Chaque composant
   * s'auto-masque quand il n'a rien à afficher.
   */
  getGlobalHeaderSlots(appCtx: AppContext): readonly LazyView[] {
    return this.getAllFeatures()
      .filter((m) => this.isEnabled(m, appCtx))
      .map((m) => m.globalHeaderSlot)
      .filter((v): v is LazyView => v !== undefined);
  }

  /**
   * Raccourcis clavier actifs dans le contexte courant — `'global'` partout
   * + `'context'` qui matchent. Filtré par `enabledWhen` au niveau feature.
   */
  getActiveShortcuts(context: Context, appCtx: AppContext): readonly ShortcutBinding[] {
    return this.getAllFeatures()
      .filter((m) => this.isEnabled(m, appCtx))
      .flatMap((m) => m.shortcuts ?? [])
      .filter((s) => {
        if (s.scope === 'global') return true;
        // scope === 'context' (défaut) : doit matcher
        return (s.contexts ?? []).includes(context);
      });
  }

  /**
   * Tous les raccourcis des features activées, quel que soit le contexte —
   * pour les lister (écran Paramètres). Ce qui est réellement écouté sur un
   * contexte donné reste `getActiveShortcuts`.
   */
  getAllShortcuts(appCtx: AppContext): readonly ShortcutBinding[] {
    return this.getAllFeatures()
      .filter((m) => this.isEnabled(m, appCtx))
      .flatMap((m) => m.shortcuts ?? []);
  }

  // ── Test helper (uniquement pour les tests) ──────────────────────────────

  /**
   * Réinitialise complètement le registry. Réservé aux tests.
   * @internal
   */
  _resetForTests(): void {
    this.features.clear();
    this.initialized = false;
  }
}

/**
 * Instance singleton. À utiliser partout dans l'app.
 */
export const featureRegistry = new FeatureRegistry();
